import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Spinner, Form, Row, Col, Button, Modal, Alert } from 'react-bootstrap';
import { useTarifariosUpdate } from '../../hooks/useTarifariosUpdate';
import { useSelector } from 'react-redux';
import { SuccessSwal } from '@shared/helpers/swal';

// Formatear monto con separadores de miles
const formatMonto = (monto) => {
    if (!monto && monto !== 0) return '';
    return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(monto);
};

const TarifariosTable = ({
    data,
    loading,
    total,
    pageSize,
    pageNumber,
    onPageChange,
    onPageSizeChange,
    tipoTarifarioId,
    tiposTarifario,
    onTipoTarifarioChange,
    permisos = {},
    children,
    onTarifarioUpdated // Callback para notificar cuando se actualiza un tarifario
}) => {
    const user = useSelector(state => state.auth.user);
    
    // Hook para actualización de tarifarios
    const { 
        updating, 
        updateError, 
        updateSingleTarifario,
        updateBulkTarifarios,
        clearUpdateError 
    } = useTarifariosUpdate(user);

    // Estados para el modal de edición
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedTarifario, setSelectedTarifario] = useState(null);
    const [editPrecio, setEditPrecio] = useState('');
    const [editModalidad, setEditModalidad] = useState('Variable');

    // Estados para edición masiva
    const [selectedTarifarios, setSelectedTarifarios] = useState(new Set());
    const [showBulkEditModal, setShowBulkEditModal] = useState(false);
    const [bulkPrecio, setBulkPrecio] = useState('');
    const [bulkModalidad, setBulkModalidad] = useState('Variable');

    // Obtener tipo de tarifario actual
    const tipoTarifarioActual = tiposTarifario.find(tipo => tipo.id == tipoTarifarioId);
    const tipoTarifario = tipoTarifarioActual?.tipo || 'ingreso'; // Por defecto ingreso

    // Funciones para manejo de selección masiva (definidas antes de getColumns)
    const handleSelectTarifario = (tarifarioId) => {
        const newSelected = new Set(selectedTarifarios);
        if (newSelected.has(tarifarioId)) {
            newSelected.delete(tarifarioId);
        } else {
            newSelected.add(tarifarioId);
        }
        setSelectedTarifarios(newSelected);
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allIds = new Set(data.map(item => item.id));
            setSelectedTarifarios(allIds);
        } else {
            setSelectedTarifarios(new Set());
        }
    };

    // Definir columnas según el tipo de tarifario
    const getColumns = () => {
        const baseColumns = [];
        
        // Columna de checkbox si tiene permisos de edición
        if (permisos.canEdit) {
            baseColumns.push({
                Header: (
                    <Form.Check
                        type="checkbox"
                        checked={selectedTarifarios.size > 0 && selectedTarifarios.size === data.length}
                        ref={input => {
                            if (input) input.indeterminate = selectedTarifarios.size > 0 && selectedTarifarios.size < data.length;
                        }}
                        onChange={handleSelectAll}
                    />
                ),
                accessor: row => (
                    <Form.Check
                        type="checkbox"
                        checked={selectedTarifarios.has(row.id)}
                        onChange={(e) => {
                            e.stopPropagation(); // Evitar que se abra el modal de edición individual
                            handleSelectTarifario(row.id);
                        }}
                        onClick={(e) => {
                            e.stopPropagation(); // Doble seguridad para evitar propagación
                        }}
                    />
                ),
                width: '50px'
            });
        }

        if (tipoTarifario === 'ingreso') {
            baseColumns.push(
                { Header: 'Cliente', accessor: row => row.cliente?.cliente || '' },
                { Header: 'Región', accessor: row => row.comuna?.region?.region || '' },
                { Header: 'Comuna', accessor: row => row.comuna?.comuna || '' },
                { Header: 'Local Cliente', accessor: row => row.local_cliente?.local || '' },
                { Header: 'Tamaño Cliente', accessor: row => row.tamano_cliente?.tamano || '' },
                { Header: 'TMS', accessor: row => row.tms?.tms || '' },
                { Header: 'Servicio Spread', accessor: row => row.servicio_spread?.servicio || '' },
                { Header: 'Tipo de Vehículo', accessor: row => row.tipo_vehiculo?.tipo || '-' },
                { Header: 'Precio', accessor: row => formatMonto(row.precio) },
                { Header: 'Modalidad', accessor: row => row.modalidad || 'Variable' }
            );
        } else {
            // Tipo egreso
            baseColumns.push(
                { Header: 'Región', accessor: row => row.comuna?.region?.region || '' },
                { Header: 'Comuna', accessor: row => row.comuna?.comuna || '' },
                { Header: 'Servicio Spread', accessor: row => row.servicio_spread?.servicio || '' },
                { Header: 'Tamaño Spread', accessor: row => row.tamano_spread?.tamano || '' },
                { Header: 'TMS', accessor: row => row.tms?.tms || '' },
                { Header: 'Centro de despacho', accessor: row => row.centro_despacho?.centroDespacho || '-' },
                { Header: 'Tipo de Vehículo', accessor: row => row.tipo_vehiculo?.tipo || '-' },
                { Header: 'Precio', accessor: row => formatMonto(row.precio) },
                { Header: 'Modalidad', accessor: row => row.modalidad || 'Variable' }
            );
        }
        
        return baseColumns;
    };

    const columns = getColumns();

    // Limpiar selección cuando cambien los datos
    useEffect(() => {
        setSelectedTarifarios(new Set());
    }, [data, pageNumber]);

    // Función para abrir modal de edición
    const handleRowClick = (tarifario) => {
        if (!permisos.canEdit) return; // Solo si tiene permisos de edición
        
        setSelectedTarifario(tarifario);
        setEditPrecio(tarifario.precio || '');
        setEditModalidad(tarifario.modalidad || 'Variable');
        setShowEditModal(true);
    };

    const handleOpenBulkEdit = () => {
        setBulkPrecio('');
        setBulkModalidad('Variable');
        setShowBulkEditModal(true);
    };

    const handleCloseBulkEdit = () => {
        setShowBulkEditModal(false);
        setBulkPrecio('');
        setBulkModalidad('Variable');
        clearUpdateError();
    };

    // Función para cerrar modal
    const handleCloseModal = () => {
        setShowEditModal(false);
        setSelectedTarifario(null);
        setEditPrecio('');
        setEditModalidad('Variable');
        clearUpdateError();
    };

    // Función para guardar cambios
    const handleSaveChanges = async () => {
        if (!selectedTarifario) return;
        
        // Limpiar errores y estado previo
        clearUpdateError();

        // Validaciones básicas antes de enviar
        if (!editPrecio || editPrecio.trim() === '') {
            return; // El hook manejará la validación
        }

        try {
            const result = await updateSingleTarifario(
                selectedTarifario.id,
                editPrecio,
                editModalidad
            );

            if (result.success) {
                // Cerrar modal inmediatamente
                handleCloseModal();
                
                // Mostrar mensaje de éxito con SweetAlert
                let successMessage = 'Tarifario actualizado exitosamente';
                
                // Si hay información detallada de la actualización, mostrarla
                if (result.data?.total_actualizados) {
                    successMessage = `Se actualizó ${result.data.total_actualizados} tarifario correctamente`;
                }
                
                SuccessSwal(
                    'Actualización Exitosa',
                    successMessage,
                    false // No recargar la página
                );
                
                // Notificar al componente padre para refrescar los datos
                if (onTarifarioUpdated) {
                    onTarifarioUpdated(selectedTarifario.id, editPrecio, editModalidad);
                }
            }
            // Los errores se manejan automáticamente por el hook y se muestran en el modal
        } catch (error) {
            console.error('Error inesperado al guardar:', error);
        }
    };

    // Función para guardar cambios masivos
    const handleSaveBulkChanges = async () => {
        if (selectedTarifarios.size === 0) return;
        
        // Limpiar errores previos
        clearUpdateError();

        // Validaciones básicas
        if (!bulkPrecio || bulkPrecio.trim() === '') {
            return; // El hook manejará la validación
        }

        try {
            // Crear array de tarifarios para actualizar
            const tarifariosToUpdate = Array.from(selectedTarifarios).map(id => ({
                id: id,
                precio: parseFloat(bulkPrecio),
                modalidad: bulkModalidad
            }));

            const result = await updateBulkTarifarios(tarifariosToUpdate);

            if (result.success) {
                // Cerrar modal inmediatamente
                handleCloseBulkEdit();
                
                // Limpiar selección
                setSelectedTarifarios(new Set());
                
                // Mostrar mensaje de éxito con SweetAlert
                const successMessage = `Se actualizaron ${result.totalActualizados} tarifarios correctamente`;
                
                SuccessSwal(
                    'Actualización Masiva Exitosa',
                    successMessage,
                    false // No recargar la página
                );
                
                // Notificar al componente padre para refrescar los datos
                if (onTarifarioUpdated) {
                    onTarifarioUpdated();
                }
            }
            // Los errores se manejan automáticamente por el hook
        } catch (error) {
            console.error('Error inesperado al guardar cambios masivos:', error);
        }
    };

    // Renderizar cabecera
    const renderHeader = () => (
        <tr>
            {columns.map((col, idx) => (
                <th key={idx}>{col.Header}</th>
            ))}
        </tr>
    );

    // Renderizar filas
    const renderRows = () => (
        data.map((row, idx) => (
            <tr 
                key={row.id || idx}
                style={{ 
                    cursor: permisos.canEdit ? 'pointer' : 'default',
                    backgroundColor: permisos.canEdit ? 'transparent' : 'inherit'
                }}
                onMouseEnter={(e) => {
                    if (permisos.canEdit) {
                        e.target.closest('tr').style.backgroundColor = '#f8f9fa';
                    }
                }}
                onMouseLeave={(e) => {
                    if (permisos.canEdit) {
                        e.target.closest('tr').style.backgroundColor = 'transparent';
                    }
                }}
            >
                {columns.map((col, cidx) => (
                    <td 
                        key={cidx}
                        onClick={cidx === 0 && permisos.canEdit ? 
                            (e) => e.stopPropagation() : // Primera columna (checkbox) - evitar propagación
                            () => handleRowClick(row) // Otras columnas - abrir modal
                        }
                        style={{
                            cursor: cidx === 0 && permisos.canEdit ? 'default' : 'inherit'
                        }}
                    >
                        {col.accessor(row)}
                    </td>
                ))}
            </tr>
        ))
    );

    // Paginación
    const pageIndex = Math.max(0, (pageNumber || 1) - 1);
    const pageCount = Math.max(1, Math.ceil(total / pageSize));

    const renderPagination = () => (
        <div className="d-flex justify-content-between align-items-center mt-2">
            <div>
                <span className="text-muted">
                    Mostrando {Math.min((pageIndex * pageSize) + 1, total)} a {Math.min((pageIndex + 1) * pageSize, total)} de {total} registros
                </span>
            </div>
            <div className="d-flex align-items-center gap-2">
                <Form.Select
                    size="sm"
                    value={pageSize}
                    onChange={e => onPageSizeChange(parseInt(e.target.value))}
                    style={{ width: 'auto' }}
                >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                </Form.Select>
                <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={pageNumber <= 1}
                    onClick={() => onPageChange(pageNumber - 1)}
                >
                    Anterior
                </Button>
                <span className="text-muted">
                    Página {pageNumber} de {pageCount}
                </span>
                <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={pageNumber >= pageCount}
                    onClick={() => onPageChange(pageNumber + 1)}
                >
                    Siguiente
                </Button>
            </div>
        </div>
    );

    // Convertir arrays a opciones para dropdowns simples
    const renderTipoTarifarioOptions = () => [
        // <option key="" value="">Seleccionar...</option>,
        ...tiposTarifario.map(tipo => (
            <option key={tipo.id} value={tipo.id}>
                {tipo.tipo} - {tipo.descripcion}
            </option>
        ))
    ];

    // Obtener tarifarios seleccionados con su información completa
    const getSelectedTarifariosData = () => {
        return data.filter(tarifario => selectedTarifarios.has(tarifario.id));
    };

    // Función para renderizar la vista simplificada de un tarifario
    const renderSimplifiedTarifario = (tarifario) => {
        if (tipoTarifario === 'ingreso') {
            return (
                <div key={tarifario.id} className="border rounded p-2 mb-2 bg-light">
                    <div className="row">
                        <div className="col-md-6">
                            <small><strong>Cliente:</strong> {tarifario.cliente?.cliente || '-'}</small><br/>
                            <small><strong>Región:</strong> {tarifario.comuna?.region?.region || '-'}</small><br/>
                            <small><strong>Comuna:</strong> {tarifario.comuna?.comuna || '-'}</small><br/>
                        </div>
                        <div className="col-md-6">
                            <small><strong>Local Cliente:</strong> {tarifario.local_cliente?.local || '-'}</small><br/>
                            <small><strong>TMS:</strong> {tarifario.tms?.tms || '-'}</small><br/>
                            <small><strong>Tipo Vehículo:</strong> {tarifario.tipo_vehiculo?.tipo || '-'}</small><br/>
                        </div>
                    </div>
                </div>
            );
        } else {
            return (
                <div key={tarifario.id} className="border rounded p-2 mb-2 bg-light">
                    <div className="row">
                        <div className="col-md-6">
                            <small><strong>Región:</strong> {tarifario.comuna?.region?.region || '-'}</small><br/>
                            <small><strong>Comuna:</strong> {tarifario.comuna?.comuna || '-'}</small><br/>
                            <small><strong>Servicio Spread:</strong> {tarifario.servicio_spread?.servicio || '-'}</small><br/>
                        </div>
                        <div className="col-md-6">
                            <small><strong>TMS:</strong> {tarifario.tms?.tms || '-'}</small><br/>
                            <small><strong>Centro despacho:</strong> {tarifario.centro_despacho?.centro || '-'}</small><br/>
                            <small><strong>Tipo Vehículo:</strong> {tarifario.tipo_vehiculo?.tipo || '-'}</small><br/>
                        </div>
                    </div>
                </div>
            );
        }
    };

    return (
        <div>
            {/* Filtros */}
            <Row className="mb-3">
                <Col md={3}>
                    <Form.Group controlId="tipoTarifarioSelect">
                        <Form.Label>
                            Tipo Tarifario
                            {tipoTarifarioActual && (
                                <span className="badge bg-primary ms-2">
                                    {tipoTarifarioActual.tipo.toUpperCase()}
                                </span>
                            )}
                        </Form.Label>
                        <Form.Select
                            value={tipoTarifarioId || ''}
                            onChange={(e) => onTipoTarifarioChange(e.target.value)}
                        >
                            {renderTipoTarifarioOptions()}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={9} className="d-flex align-items-end justify-content-end gap-2">
                    {/* Botón de edición masiva */}
                    {permisos.canEdit && selectedTarifarios.size > 0 && (
                        <Button 
                            variant="outline-primary" 
                            onClick={handleOpenBulkEdit}
                            className="d-flex align-items-center"
                        >
                            <i className="fas fa-edit me-2"></i>
                            Editar ({selectedTarifarios.size})
                        </Button>
                    )}
                    
                    {/* El botón de filtros se agregará aquí desde el componente padre */}
                    {children}
                </Col>
            </Row>

            {/* Tabla */}
            <div className="table-responsive">
                {/* {permisos.canEdit && (
                    <div className="alert alert-info mb-2">
                        <i className="fas fa-info-circle me-2"></i>
                        Haga click en cualquier fila para editar el precio y modalidad del tarifario.
                    </div>
                )} */}
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                        <Spinner animation="border" />
                    </div>
                ) : (
                    <table className="table table-striped table-hover">
                        <thead>
                            {renderHeader()}
                        </thead>
                        <tbody>
                            {data.length > 0 ? renderRows() : (
                                <tr>
                                    <td colSpan={columns.length} className="text-center">
                                        No se encontraron registros
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Paginación */}
            {!loading && data.length > 0 && renderPagination()}

            {/* Modal de edición */}
            <Modal show={showEditModal} onHide={handleCloseModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Editar Tarifario - {tipoTarifarioActual?.tipo || 'Tarifario'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedTarifario && (
                        <div>
                            {/* Mensajes de error */}
                            {updateError && (
                                <Alert variant="danger" className="mb-3">
                                    <i className="fas fa-exclamation-triangle me-2"></i>
                                    {updateError}
                                </Alert>
                            )}

                            {/* Información del tarifario */}
                            <div className="mb-4">
                                <h6 className="mb-3">Información del Tarifario:</h6>
                                <Row>
                                    {tipoTarifario === 'ingreso' ? (
                                        <>
                                            <Col md={6}>
                                                <p><strong>Cliente:</strong> {selectedTarifario.cliente?.cliente || '-'}</p>
                                                <p><strong>Región:</strong> {selectedTarifario.comuna?.region?.region || '-'}</p>
                                                <p><strong>Comuna:</strong> {selectedTarifario.comuna?.comuna || '-'}</p>
                                                <p><strong>Local Cliente:</strong> {selectedTarifario.local_cliente?.local || '-'}</p>
                                                <p><strong>Tamaño Cliente:</strong> {selectedTarifario.tamano_cliente?.tamano || '-'}</p>
                                            </Col>
                                            <Col md={6}>
                                                <p><strong>TMS:</strong> {selectedTarifario.tms?.tms || '-'}</p>
                                                <p><strong>Tipo de Vehículo:</strong> {selectedTarifario.tipo_vehiculo?.tipo || '-'}</p>
                                            </Col>
                                        </>
                                    ) : (
                                        <>
                                            <Col md={6}>
                                                <p><strong>Región:</strong> {selectedTarifario.comuna?.region?.region || '-'}</p>
                                                <p><strong>Comuna:</strong> {selectedTarifario.comuna?.comuna || '-'}</p>
                                                <p><strong>Servicio Spread:</strong> {selectedTarifario.servicio_spread?.servicio || '-'}</p>
                                                <p><strong>Tamaño Spread:</strong> {selectedTarifario.tamano_spread?.tamano || '-'}</p>
                                            </Col>
                                            <Col md={6}>
                                                <p><strong>TMS:</strong> {selectedTarifario.tms?.tms || '-'}</p>
                                                <p><strong>Centro de despacho:</strong> {selectedTarifario.centro_despacho?.centro || '-'}</p>
                                                <p><strong>Tipo de Vehículo:</strong> {selectedTarifario.tipo_vehiculo?.tipo || '-'}</p>
                                            </Col>
                                        </>
                                    )}
                                </Row>
                            </div>

                            {/* Campos editables */}
                            <div className="border-top pt-4">
                                <h6 className="mb-3">Campos Editables:</h6>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Precio</Form.Label>
                                            <Form.Control
                                                type="number"
                                                value={editPrecio}
                                                onChange={(e) => setEditPrecio(e.target.value)}
                                                placeholder="Ingrese el precio"
                                                min="0"
                                                step="1"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Modalidad</Form.Label>
                                            <Form.Select
                                                value={editModalidad}
                                                onChange={(e) => setEditModalidad(e.target.value)}
                                            >
                                                <option value="Variable">Variable</option>
                                                <option value="Fijo">Fijo</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal} disabled={updating}>
                        Cancelar
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleSaveChanges}
                        disabled={updating}
                    >
                        {updating ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Guardando...
                            </>
                        ) : (
                            'Guardar Cambios'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de edición masiva */}
            <Modal show={showBulkEditModal} onHide={handleCloseBulkEdit} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        Edición Masiva - {selectedTarifarios.size} tarifario(s) seleccionado(s)
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {/* Mensajes de error */}
                    {updateError && (
                        <Alert variant="danger" className="mb-3">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            {updateError}
                        </Alert>
                    )}

                    {/* Campos de edición masiva */}
                    <div className="mb-4">
                        <h6 className="mb-3">Nuevos Valores para Todos los Tarifarios:</h6>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Precio</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={bulkPrecio}
                                        onChange={(e) => setBulkPrecio(e.target.value)}
                                        placeholder="Ingrese el precio"
                                        min="0"
                                        step="1"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Modalidad</Form.Label>
                                    <Form.Select
                                        value={bulkModalidad}
                                        onChange={(e) => setBulkModalidad(e.target.value)}
                                    >
                                        <option value="Variable">Variable</option>
                                        <option value="Fijo">Fijo</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                    </div>

                    {/* Lista de tarifarios seleccionados */}
                    <div className="border-top pt-4">
                        <h6 className="mb-3">Tarifarios que serán actualizados:</h6>
                        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            {getSelectedTarifariosData().map(tarifario => renderSimplifiedTarifario(tarifario))}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseBulkEdit} disabled={updating}>
                        Cancelar
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleSaveBulkChanges}
                        disabled={updating}
                    >
                        {updating ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Guardando...
                            </>
                        ) : (
                            `Actualizar ${selectedTarifarios.size} Tarifario(s)`
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

TarifariosTable.propTypes = {
    data: PropTypes.array.isRequired,
    loading: PropTypes.bool,
    total: PropTypes.number,
    pageSize: PropTypes.number,
    pageNumber: PropTypes.number,
    onPageChange: PropTypes.func,
    onPageSizeChange: PropTypes.func,
    tipoTarifarioId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    tiposTarifario: PropTypes.array,
    onTipoTarifarioChange: PropTypes.func,
    permisos: PropTypes.object,
    children: PropTypes.node,
    onTarifarioUpdated: PropTypes.func
};

export default TarifariosTable;
